import { apiClient } from './config.js';

/**
 * LinkedIn Community Management API connection (Milestone A, 2026-09-20).
 * Backend: /admin/linkedin/* — super_admin only.
 */

export interface LinkedInMasthead {
  target_id: string;
  name: string | null;
  linkedin_urn: string | null;
  /** null when no token is connected. */
  administered: boolean | null;
}

export interface LinkedInQuotaRow {
  endpoint_key: string;
  used_today: number;
  cap: number | null;
  app_cap: number | null;
  user_cap: number | null;
}

export interface LinkedInTokenInfo {
  member_urn: string | null;
  consented_by: string | null;
  consented_at: string | null;
  scopes: string;
  access_expires_at: string;
  access_expires_in_days: number;
  refresh_expires_at: string | null;
  reconsent_due_in_days: number | null;
  last_refreshed_at: string | null;
  last_used_at: string | null;
  last_error: string | null;
}

export interface LinkedInStatus {
  connected: boolean;
  client_id_configured: boolean;
  api_version: string;
  redirect_uri: string;
  token: LinkedInTokenInfo | null;
  mastheads: LinkedInMasthead[];
  all_mastheads_administered: boolean;
  quota_today_utc: LinkedInQuotaRow[];
  resolve_budget: number;
  /** P5 (CS538): the drafts page shows "Post natively (test Page)" only when true. Absent before backend bd2b74d7d. */
  native_posting_enabled?: boolean;
  native_posting_target?: string;
}

/** P5 (CS538): POST /admin/linkedin/posts/native response. */
export interface NativePostResult {
  post_urn: string;
  url: string;
  target: string;
  mentions: number;
  image_urn: string | null;
  image_note: string | null;
}

export async function getLinkedInStatus(): Promise<LinkedInStatus> {
  const { data } = await apiClient.get<LinkedInStatus>('/admin/linkedin/status');
  return data;
}

export async function startLinkedInOAuth(): Promise<{ authorization_url: string; state: string }> {
  const { data } = await apiClient.post('/admin/linkedin/oauth/start');
  return data;
}

export async function exchangeLinkedInCode(code: string, state: string): Promise<LinkedInStatus> {
  const { data } = await apiClient.post<LinkedInStatus>('/admin/linkedin/oauth/exchange', { code, state });
  return data;
}

export async function revokeLinkedInToken(): Promise<LinkedInStatus> {
  const { data } = await apiClient.post<LinkedInStatus>('/admin/linkedin/revoke');
  return data;
}

/** P5 (CS538): post a draft natively to the LinkedIn test Page (DevTestCo); the draft and Buffer are not changed. */
export async function postLinkedInNative(socialPostId: number): Promise<NativePostResult> {
  const { data } = await apiClient.post<NativePostResult>('/admin/linkedin/posts/native', { social_post_id: socialPostId });
  return data;
}
